import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import {
  cancelAllRuns,
  cancelRun,
  fetchActiveRuns,
  fetchRunHistory,
} from '../../services/activeRunsService';
import { selectSelectedDevice } from '../../redux/app/platformSlice';
import { getDeviceIdForAppiumScripts } from '../../utilities/getDevideIdForAppiumScripts';

export function useActiveRunsLogic() {
  const [activeData, setActiveData] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const selectedDevice = useSelector(selectSelectedDevice);
  const prevActiveCountRef = useRef(null);

  const deviceId = useMemo(() => getDeviceIdForAppiumScripts(selectedDevice), [selectedDevice]);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetchRunHistory(500, deviceId);
      setHistoryData(res.runs || []);
    } catch (e) {
      console.error('Failed to load run history', e);
    }
  }, [deviceId]);

  useEffect(() => {
    let mounted = true;

    const loadActive = async () => {
      try {
        const res = await fetchActiveRuns(deviceId);
        if (!mounted) return;

        const runs = res.runs || [];
        setActiveData(runs);
        setLoading(false);

        const currentCount = runs.length;
        if (prevActiveCountRef.current !== null && currentCount < prevActiveCountRef.current) {
          loadHistory();
        }
        prevActiveCountRef.current = currentCount;
      } catch (e) {
        console.error(e);
        if (mounted) {
          setError('Failed to load runs');
          setLoading(false);
        }
      }
    };

    loadActive();
    loadHistory();

    const id = setInterval(loadActive, 2000);

    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [deviceId, loadHistory]);

  const activeRuns = useMemo(() => activeData.filter((r) => r.status === 'running'), [activeData]);

  const queuedRuns = useMemo(
    () =>
      activeData
        .filter((r) => r.status === 'queued')
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [activeData],
  );

  const handleCancelRun = async (id) => {
    try {
      setIsCancelling(true);
      await cancelRun(id);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelAll = async () => {
    try {
      setIsCancelling(true);
      await cancelAllRuns();
    } finally {
      setIsCancelling(false);
    }
  };

  return {
    activeRuns,
    currentRun: activeRuns[0],
    error,
    handleCancelAll,
    handleCancelRun,
    isCancelling,
    loading,
    queuedRuns,
    recentRuns: historyData,
    deviceId,
  };
}
